import { observer } from "mobx-react";
import React from "react";
import { NavigateFunction } from "react-router-dom";
import DollarIcon from "../../../../../images/sidebar/dollar-royal-blue.svg";
import EditIcon from "../../../../../images/products/edit-blue.svg";
import GreyLockIcon from "../../../../../images/membership/grey-lock.svg";
import CopyIcon from "../../../../../images/membership/copy-blue.svg";
import CheckMarkIcon from "../../../../../images/membership/circle-check-mark.svg";
import { Carousel, Col, Row } from "react-bootstrap";
import AdvancedSection from "./AdvancedSection";
import TrashIcon from "../../../../../images/products/trash-blue.svg";
import { styled } from "styled-components";
import TermsSection from "./TermsSection";
import RecurringSection from "./RecurringSection";
import SetupSection from "./SetupSection";
import { PricingPageService } from "../services";
import {
  ItemDetailInputModel,
  PackagePricingInputDataModel,
  PricePageRecurringInputModel,
  PricePageTermsInputModel,
  PricingPageInputModel,
} from "../models";
import { ItemBillingType, PricingModel } from "../enums";
import {
  Collapsible,
  CustomLabel,
  FlexContainer,
  Input,
  SapphireBlueButton,
  StatusCircle,
  theme,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import CarouselComponent from "./CarouselComponent";
import {
  AdvertisingBlockWrapper,
  ItemContainer,
  MainContent,
  MembershipRightBar,
  MembershipTierDetails,
  OptionSelectorCard,
} from "../styles";
import ItemHeader from "./ItemHeader";
import { AccountSettingsStore } from "../store";
import PurchasePriceDescriptionRow from "./PurchasePriceDescriptionRow";

type Props = {
  pricingPageData: PricingPageService;
  navigate: NavigateFunction;
  headerCaption?: string;
  itemName: string;
  showPricingOptions?: boolean;
  hideTermPlusRecurrence?: boolean;
  detailPage: ItemDetailInputModel;
  descriptionHtml?: string;
  isServicePricingPage?: boolean;
};
@observer
class PricingBuilder extends React.Component<Props> {
  sectionByBillingType(
    index: number,
    price: PricingPageInputModel
  ): React.ReactNode {
    switch (price.billingType) {
      case ItemBillingType.Term:
        return (
          <>
            <TermsSection
              showPricingOptions={this.props.showPricingOptions}
              index={index}
              pricingPageData={this.props.pricingPageData}
              isServicePricingPage={this.props.isServicePricingPage}
            />
          </>
        );
      case ItemBillingType.Recurring:
        return (
          <>
            <RecurringSection
              showPricingOptions={this.props.showPricingOptions}
              index={index}
              pricingPageData={this.props.pricingPageData}
            />
            <AdvancedSection
              index={index}
              pricingPageData={this.props.pricingPageData}
            />
          </>
        );
      case ItemBillingType.TermPlusRecurring:
        return (
          <>
            <TermsSection
              showPricingOptions={this.props.showPricingOptions}
              index={index}
              pricingPageData={this.props.pricingPageData}
            />
            <RecurringSection
              showPricingOptions={this.props.showPricingOptions}
              index={index}
              pricingPageData={this.props.pricingPageData}
            />
            <AdvancedSection
              index={index}
              pricingPageData={this.props.pricingPageData}
            />
          </>
        );
    }
  }

  getHeaderTitle(price: PricingPageInputModel) {
    return price.name || "";
  }

  formatPrice = (priceValue: string) => (
    <>
      <span className="semi-bold">
        ${AccountSettingsStore.formatAmount(parseFloat(priceValue) || 0, true)}
      </span>
      <span className="usd-label"> USD</span>
    </>
  );

  handleTermPricing = (termSettings?: PricePageTermsInputModel | null) => {
    if (!termSettings) return "";
    const termPricingModel = termSettings?.pricingModel;
    switch (termPricingModel) {
      case PricingModel.Standard:
        const standardPrice =
          parseFloat(termSettings?.standardPricing?.price || "0.00") +
          parseFloat(termSettings?.standardPricing?.setUpFee || "0.00");
        return <>{this.formatPrice(standardPrice.toString() || "")}</>;
      case PricingModel.Volume:
        const volumePrice =
          parseFloat(termSettings?.volumePricing?.price || "0.00") +
          parseFloat(termSettings?.volumePricing?.setUpFee || "0.00");
        return <>{this.formatPrice(volumePrice.toString())}</>;
      case PricingModel.Tiered:
        const tieredPrice =
          parseFloat(termSettings?.tieredPricing?.price || "0.00") +
          parseFloat(termSettings?.tieredPricing?.setUpFee || "0.00");
        return <>{this.formatPrice(tieredPrice.toString())}</>;
      case PricingModel.Custom:
        return "";
      case PricingModel.Package:
        const packagePrice =
          parseFloat(termSettings?.packagePricing?.options[0].price || "0.00") +
          parseFloat(termSettings?.packagePricing?.setUpFee || "0.00");
        return <>{this.formatPrice(packagePrice.toString())}</>;
    }
  };

  handleRecurrencePricing = (
    recurringSettings?: PricePageRecurringInputModel | null
  ) => {
    if (!recurringSettings) return "";
    const recurrencePricingModel = recurringSettings?.pricingModel;
    if (!recurrencePricingModel) return "";
    switch (recurrencePricingModel) {
      case PricingModel.Standard:
        const price =
          parseFloat(recurringSettings?.standardPricing?.price || "0.00") +
          parseFloat(recurringSettings?.standardPricing?.setUpFee || "0.00");
        return (
          <>
            {this.formatPrice(price.toString() || "")} +{" "}
            {this.formatPrice(recurringSettings?.standardPricing?.price || "")}
          </>
        );
      case PricingModel.Volume:
        const recurringPrice =
          parseFloat(recurringSettings?.volumePricing?.price || "0.00") +
          parseFloat(recurringSettings?.volumePricing?.setUpFee || "0.00");
        return (
          <>
            {this.formatPrice(recurringPrice.toString())} +{" "}
            {this.formatPrice(recurringSettings?.volumePricing?.price || "")}
          </>
        );
      case PricingModel.Tiered:
        const tieredPrice =
          parseFloat(recurringSettings?.tieredPricing?.price || "0.00") +
          parseFloat(recurringSettings?.tieredPricing?.setUpFee || "0.00");
        return (
          <>
            {this.formatPrice(tieredPrice.toString())} +{" "}
            {this.formatPrice(recurringSettings?.tieredPricing?.price || "")}
          </>
        );
      case PricingModel.Custom:
        return "";
      case PricingModel.Package:
        const packagePrice =
          parseFloat(
            recurringSettings?.packagePricing?.options[0].price || "0.00"
          ) + parseFloat(recurringSettings?.packagePricing?.setUpFee || "0.00");
        return (
          <>
            {this.formatPrice(packagePrice.toString())} +{" "}
            {this.formatPrice(
              recurringSettings?.packagePricing?.options[0]?.price.toString() ||
                "0.00"
            )}
          </>
        );
    }
  };

  handleTermPlusRecurrencePricing = (
    termSettings?: PricePageTermsInputModel | null,
    recurringSettings?: PricePageRecurringInputModel | null
  ) => {
    if (!termSettings || !recurringSettings) return "";
    const termPrice = termSettings?.standardPricing?.price || "0.00";
    const recurringPrice = recurringSettings?.standardPricing?.price || "0.00";
    const termSetUpFee = termSettings?.standardPricing?.setUpFee || "0.00";
    const recurringSetUpFee =
      recurringSettings?.standardPricing?.setUpFee || "0.00";

    const totalTermPrice = parseFloat(termPrice) + parseFloat(termSetUpFee);
    const totalRecurringPrice =
      parseFloat(recurringPrice) + parseFloat(recurringSetUpFee);

    if (totalRecurringPrice || totalTermPrice) {
      return (
        <>
          {this.formatPrice(
            (totalTermPrice + totalRecurringPrice).toString() || ""
          )}{" "}
          + ${<span className="semi-bold">{recurringPrice}</span>}{" "}
          <span className="usd-label">USD</span>
        </>
      );
    }
    return "";
  };

  getAdvertisingPrice(price: PricingPageInputModel) {
    const { termSettings, recurringSettings, billingType } = price;

    switch (billingType) {
      case ItemBillingType.Term:
        return <>{this.handleTermPricing(termSettings)}</>;
      case ItemBillingType.Recurring:
        return (
          <>
            {this.handleRecurrencePricing(recurringSettings)} /{" "}
            <span className="usd-label">
              {recurringSettings?.billingIntervalCount || ""}{" "}
              {recurringSettings?.billingInterval || ""}
              {parseFloat(recurringSettings?.billingIntervalCount || "0") > 1
                ? "s"
                : ""}
            </span>
          </>
        );
      case ItemBillingType.TermPlusRecurring:
        return (
          <>
            {this.handleTermPlusRecurrencePricing(
              termSettings,
              recurringSettings
            )}{" "}
            <span className="usd-label">
              / every {recurringSettings?.billingIntervalCount}{" "}
              {recurringSettings?.billingInterval || ""}
              {parseFloat(recurringSettings?.billingIntervalCount || "0.00") > 1
                ? "s"
                : ""}
            </span>
          </>
        );
      default:
        return "";
    }
  }

  getOriginalPrice(price: PricingPageInputModel): string {
    const { termSettings, recurringSettings, billingType } = price;
    const termPricingModel = termSettings?.pricingModel;
    const recurrencePricingModel = recurringSettings?.pricingModel;
    const termPrice = termSettings?.standardPricing?.originalPrice || "";
    const recurringPrice =
      recurringSettings?.standardPricing?.originalPrice || "";

    const getTotalOriginalPrice = (options: PackagePricingInputDataModel[]) => {
      return options[0]?.originalPrice || "0.00";
    };

    const handleTermPricing = () => {
      switch (termPricingModel) {
        case PricingModel.Standard:
          return termSettings?.standardPricing?.originalPrice || "";
        case PricingModel.Volume:
          return termSettings?.volumePricing?.originalPrice || "";
        case PricingModel.Tiered:
          return termSettings?.tieredPricing?.originalPrice || "";
        case PricingModel.Custom:
          return termSettings?.standardPricing?.originalPrice || "";
        case PricingModel.Package:
          return (
            getTotalOriginalPrice(
              termSettings?.packagePricing?.options || []
            ).toString() || ""
          );
        default:
          return "";
      }
    };

    const handleRecurrencePricing = () => {
      switch (recurrencePricingModel) {
        case PricingModel.Standard:
          return recurringSettings?.standardPricing?.originalPrice || "";
        case PricingModel.Volume:
          return recurringSettings?.volumePricing?.originalPrice || "";
        case PricingModel.Tiered:
          return recurringSettings?.tieredPricing?.originalPrice || "";
        case PricingModel.Custom:
          return recurringSettings?.standardPricing?.originalPrice || "";
        case PricingModel.Package:
          return (
            getTotalOriginalPrice(
              recurringSettings?.packagePricing?.options || []
            ).toString() || ""
          );
        default:
          return "";
      }
    };

    const handleTermPlusRecurrencePricing = () => {
      if (termPrice && recurringPrice) {
        const totalOriginalPrice =
          parseFloat(termPrice) + parseFloat(recurringPrice);
        return totalOriginalPrice > 0
          ? AccountSettingsStore.formatAmount(totalOriginalPrice)
          : "";
      }
      return "";
    };

    switch (billingType) {
      case ItemBillingType.Term:
        return handleTermPricing();
      case ItemBillingType.Recurring:
        return handleRecurrencePricing();
      case ItemBillingType.TermPlusRecurring:
        return handleTermPlusRecurrencePricing();
      default:
        return "";
    }
  }

  priceHeader(price: PricingPageInputModel, index: number) {
    return (
      <Row
        key={index}
        className="selected"
        onClick={() => {
          this.toggleIsOpen(price, index);
        }}
      >
        <Col md={4}>
          <FlexContainer spacing={20} alignItems="center">
            <img src={DollarIcon} alt="dollar" />
            <CustomLabel
              fontSize="18px"
              color="#5566bb"
              fontWeight="600"
              className="overflow-ellipsis"
              lineHeight="26px"
            >
              {this.getHeaderTitle(price)}
            </CustomLabel>
          </FlexContainer>
        </Col>
        <Col md={6}>
          <FlexContainer alignItems="center">
            <StatusCircle heightWidth="14px" inActive={!price.isActive} />
            <CustomLabel
              fontSize="18px"
              color={theme.palette.common.black}
              className="overflow-ellipsis "
              lineHeight="26px"
              fontWeight="600"
              margin="0px 0px 0px 10px"
            >
              {this.getAdvertisingPrice(price)}
            </CustomLabel>
            <img src={GreyLockIcon} className="lock-icon" alt="lock" />
            {price.isDefault && (
              <img src={CheckMarkIcon} className="check-icon" alt="check" />
            )}
          </FlexContainer>
        </Col>
        <Col md={2} className="icons-column">
          <img
            src={EditIcon}
            onClick={(e) => {
              e.stopPropagation();
              this.toggleIsOpen(price, index);
            }}
            alt="Edit Price"
          />
          <img
            src={CopyIcon}
            onClick={(e) => {
              e.stopPropagation();
              this.props.pricingPageData.copyPrice(index);
            }}
            alt="Copy Price"
          />
          {index > 0 && (
            <img
              src={TrashIcon}
              onClick={(e) => {
                e.stopPropagation();
                this.props.pricingPageData.removePrice(index);
              }}
              alt="Delete Price"
            />
          )}
        </Col>
      </Row>
    );
  }

  toggleIsOpen = (price: PricingPageInputModel, index: number) => {
    this.props.pricingPageData.updatePrice(
      new PricingPageInputModel({
        ...price,
        isOpen: !price.isOpen,
      }),
      index
    );
  };

  get mediaSection() {
    const { medias } = this.props.detailPage;

    return <CarouselComponent media={medias} />;
  }

  textField(
    type: "text" | "number",
    field: string,
    placeHolder: string,
    value: string,
    hasError: boolean,
    className?: string
  ): React.ReactNode {
    return (
      <Input
        value={value}
        placeholder={placeHolder}
        id={field}
        type={type}
        error={hasError}
        onChange={(e) => {}}
        className={className}
      />
    );
  }

  OptionSelectorCard = (price: PricingPageInputModel, index: number) => {
    return (
      <OptionSelectorCard className="ad-selector-card">
        <CustomLabel
          fontSize="22px"
          fontWeight="600"
          color="black"
          lineHeight="24px"
          textAlign="center"
          margin="0 0 5px"
        >
          {this.props.pricingPageData.prices[index].name}
        </CustomLabel>
        <CustomLabel
          fontSize="12px"
          textAlign="center"
          margin="6px 0 15px"
          lineHeight="16px"
        >
          {this.props.pricingPageData.prices[index].description}
        </CustomLabel>
        <CustomLabel className="actual-price" margin="0px 0px 30px">
          {this.getAdvertisingPrice(price)}
        </CustomLabel>
        {this.getOriginalPrice(price) &&
          this.getOriginalPrice(price) !== "0.00" && (
            <CustomLabel
              className="actual-price"
              margin="0px 0px 30px"
              fontSize={"13px"}
              color="#FF5900"
              style={{ color: "#FF5900", fontSize: "13px" }}
            >
              Reduced from ${this.getOriginalPrice(price)}
            </CustomLabel>
          )}
        <SapphireBlueButton
          primary
          borderRadius="20px"
          fontSize="15px"
          lineHeight="10px"
          minWidth="90px"
        >
          Select
        </SapphireBlueButton>
      </OptionSelectorCard>
    );
  };

  render(): React.ReactNode {
    return (
      <MainContent className="main-content">
        <div className="flex-auto-grow">
          <ItemContainer padding="0px 25px 0px 0px" className="float-aside">
            <ItemHeader
              header={this.props.headerCaption || ""}
              showCopyButton={true}
              showPriceButton={true}
              onAddPriceClick={() => this.props.pricingPageData.addPrice()}
            />
            {/* <ItemContentBody noScroll> */}
            <CustomLabel
              fontSize="20px"
              fontWeight="bold"
              lineHeight="24px"
              margin="0px 0px 30px"
            >
              {this.props.itemName}
            </CustomLabel>
            <MembershipTierDetails className="membership-tier">
              {this.props.pricingPageData.prices.map((price, index) => (
                <React.Fragment key={index}>
                  {this.priceHeader(price, index)}
                  <Collapsible isOpen={price.isOpen}>
                    <SetupSection
                      index={index}
                      pricingPageData={this.props.pricingPageData}
                      hideTermPlusRecurrence={this.props.hideTermPlusRecurrence}
                    />
                    {this.sectionByBillingType(index, price)}
                  </Collapsible>
                </React.Fragment>
              ))}
            </MembershipTierDetails>
            {/* </ItemContentBody> */}
          </ItemContainer>
        </div>
        <MembershipRightBar className="float-block float-for-ss" width="35%">
          <AdvertisingBlockWrapper width="100%" maxWidth="100%">
            <div className="full-width adv-full-view">
              {this.mediaSection}
              <CustomLabel
                fontSize="22px"
                fontWeight="600"
                color={theme.palette.common.black}
                lineHeight="24px"
                textAlign="center"
                margin="50px auto 15px"
                className="full-width"
              >
                {this.props.itemName}
              </CustomLabel>
              <div
                dangerouslySetInnerHTML={{
                  __html: this.props.descriptionHtml || "",
                }}
              ></div>
              {/* <CustomLabel fontSize="14px" textAlign="center" margin="0px auto 35px">
                            Get <b>more</b> leads today for your business with our amazing{' '}
                            <span className="radical-red">consulting program </span>.
                        </CustomLabel> */}
              <StyledCarousel
                nextIcon={<i className="fa fa-solid fa-caret-right"></i>}
                prevIcon={<i className="fa fa-solid fa-caret-left"></i>}
              >
                {this.props.pricingPageData.prices.map((price, index) => {
                  return (
                    <Carousel.Item key={index}>
                      <>
                        {(price.billingType === "Term" &&
                          price.termSettings?.pricingModel ===
                            PricingModel.Custom) ||
                        (price.billingType === "Recurring" &&
                          price.recurringSettings?.pricingModel ===
                            PricingModel.Custom) ? (
                          <OptionSelectorCard>
                            <CustomLabel
                              fontSize="20px"
                              fontWeight="600"
                              color={theme.palette.common.black}
                              lineHeight="24px"
                              textAlign="center"
                              margin="0 0 5px"
                            >
                              Enter a Price
                            </CustomLabel>
                            <CustomLabel
                              fontSize="12px"
                              lineHeight="16px"
                              textAlign="center"
                              margin="0 0 15px"
                            >
                              Choose your own amount to purchase this item.
                            </CustomLabel>
                            <FlexContainer
                              spacing={15}
                              className="usd-input product-pricing-row"
                            >
                              <div>
                                {this.textField(
                                  "text",
                                  "",
                                  "",
                                  "",
                                  false,
                                  "styled-input"
                                )}
                              </div>
                            </FlexContainer>

                            <SapphireBlueButton
                              primary
                              borderRadius="20px"
                              fontSize="15px"
                              lineHeight="10px"
                              minWidth="90px"
                              style={{ minHeight: "30px" }}
                            >
                              Buy
                            </SapphireBlueButton>
                          </OptionSelectorCard>
                        ) : (
                          this.OptionSelectorCard(price, index)
                        )}
                      </>
                    </Carousel.Item>
                  );
                })}
              </StyledCarousel>
            </div>
          </AdvertisingBlockWrapper>
        </MembershipRightBar>
      </MainContent>
    );
  }
}

export default withRouter(PricingBuilder);

export const StyledCarousel = styled(Carousel)`
  .carousel-control-next,
  .carousel-control-prev {
    width: auto;
  }
  &.carousel.slide {
    display: flex;
    width: 100%;
    /* overflow-y: hidden; */
    margin: -15px;
    padding: 15px;
    overflow: hidden;
    margin: 0 auto; // for the previews in right panel
  }
  .fa.fa-solid {
    &.fa-caret-left,
    &.fa-caret-right {
      color: rgba(85, 107, 184, 0.5);
      font-size: 50px;
    }
  }
  .carousel-inner {
    width: 285px;
    margin: 0 auto;
    overflow: unset;
  }
  .carousel-indicators {
    display: none;
    width: 10px;
    height: 10px;
    background-color: blue;
    border-radius: 50%;
  }
  .carousel-item {
    visibility: hidden;
    transition: transform 0.2s ease-in-out;
    &.active {
      display: inline-block;
      visibility: visible;
      /* transition: visibility 0s linear 0.2s; */
      transition: transform 0.25s ease-in-out;
      /* For smooth transition remove box-shadow and overflow */
    }
  }
`;
